import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '@store/useUserStore';
import { ACCESS_TOKEN_CHANGE_EVENT, TokenManager, sendPushRouteAck } from 'utils/api';
import { COLD_START_ENTRY_STATE } from 'hooks/useGoBack';

const SHARING_PREFIX = '/server/buccl_sharing';

// 인증이 필요한 목적지를 보관할 때 사용자를 보내는 곳.
const LOGIN_PATH = '/login';

// 네이티브 재전달이 겹치는 짧은 구간에서만 이동 중복을 막는다.
// 마운트 내내 기록을 들고 있으면 같은 알림을 한참 뒤 다시 탭해도 화면이 열리지 않는다 —
// 그건 중복 방지가 아니라 기능 상실이다.
const ROUTED_DEDUPE_WINDOW_MS = 5000;

// 로그인 없이는 진입 즉시 /login 으로 튕겨 목적지가 유실되는 경로.
// 여기 걸리면 즉시 이동하지 않고 pending 으로 보관했다가 로그인 완료 시 소비한다.
// 빠뜨리면 목적지를 잃고도 routed(=네이티브 terminal 소비) 를 보내 복구가 불가능해진다.
// 대상 판별 기준은 페이지 최상단의 <Navigate to="/login" replace /> 가드 유무다.
// 예약 상세(/reservation/*)는 미로그인에서도 렌더되므로 대상이 아니다.
const AUTH_REQUIRED_PREFIXES = ['/messages', '/lesson-requests'];

const requiresAuth = (path) =>
  AUTH_REQUIRED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)
  );

// 공유 딥링크 prefix 제거. 이동 경로와 ACK 의 action_url 이 같은 값이어야 네이티브가
// "던진 목적지"와 "FE 가 도달한 목적지"를 대조할 수 있으므로 한 곳에서만 정의한다.
const stripSharingPrefix = (path) =>
  path.startsWith(SHARING_PREFIX) ? path.slice(SHARING_PREFIX.length) || '/' : path;

const normalizeLegacyLessonRequestPath = (path) => {
  const withoutSharingPrefix = stripSharingPrefix(path);
  const legacyStudentMatch = withoutSharingPrefix.match(/^\/lesson-request\/(\d+)(\?.*)?$/);
  if (legacyStudentMatch) {
    return `/lesson-requests/${legacyStudentMatch[1]}${legacyStudentMatch[2] || ''}`;
  }
  const legacyInstructorMatch = withoutSharingPrefix.match(/^\/instructor\/lesson-requests\/(\d+)(\?.*)?$/);
  if (legacyInstructorMatch) {
    return `/lesson-requests/${legacyInstructorMatch[1]}${legacyInstructorMatch[2] || ''}`;
  }
  return withoutSharingPrefix;
};

const resolveInternalPath = (raw) => {
  if (!raw) return null;

  let path;
  try {
    const url = new URL(raw, window.location.origin);
    path = url.pathname + url.search;
  } catch {
    path = raw;
  }

  return path.startsWith('/') ? normalizeLegacyLessonRequestPath(path) : null;
};

const getPayloadValue = (data, ...keys) => {
  const payloads = [data, data?.extra_data, data?.extraData, data?.data].filter(Boolean);
  for (const payload of payloads) {
    for (const key of keys) {
      const value = payload?.[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return undefined;
};

const buildLessonRequestNotificationPath = (data = {}) => {
  const notificationType = String(getPayloadValue(data, 'notification_type', 'notificationType', 'type') || '');
  const upperType = notificationType.toUpperCase();
  const requestId = getPayloadValue(data, 'request_id', 'requestId', 'related_object_id', 'relatedObjectId');
  const objectType = String(getPayloadValue(data, 'related_object_type', 'relatedObjectType') || '').toLowerCase();

  if (upperType === 'LESSON_REQUEST_OPENED' || upperType === 'LESSON_REQUEST_ACCEPTED') {
    const practiceScheduleId = getPayloadValue(data, 'practice_schedule_id', 'practiceScheduleId');
    const scheduleId = getPayloadValue(data, 'schedule_id', 'scheduleId');
    if (practiceScheduleId) return `/reservation/practice/${encodeURIComponent(practiceScheduleId)}`;
    if (scheduleId) return `/reservation/class/${encodeURIComponent(scheduleId)}`;
    // 구버전/누락 payload 는 기존 신청 상세로 fallback 한다.
    if (requestId) return `/lesson-requests/${encodeURIComponent(requestId)}`;
  }

  if (upperType === 'LESSON_REQUEST_REJECTED') return '/lesson-requests?status=REJECTED';
  if (upperType === 'LESSON_REQUEST_EXPIRED') return '/lesson-requests?status=EXPIRED';
  if (upperType === 'LESSON_REQUEST_CANCELLED' || upperType === 'LESSON_REQUEST_CANCELED') {
    return '/lesson-requests?status=CANCELLED';
  }

  if (requestId && (objectType === 'lessonrequest' || objectType === 'lesson_request' || data.request_id || data.requestId)) {
    return `/lesson-requests/${encodeURIComponent(requestId)}`;
  }

  return null;
};

// 푸시 payload는 발신 채널마다 키가 달라 우선순위로 방어한다.
// path → action_url → actionUrl(앱 경로 형태) → deeplink → deep_link(절대 URL 가능).
// 모든 후보를 동일하게 정규화한다: origin을 버리고 앱 내부 경로(pathname+search)만 취해
// 외부 절대/프로토콜-상대 URL이 와도 내부 라우팅으로 환원하고, 내부 경로(/로 시작)가
// 아니면 navigate 대상에서 제외한다.
// 주의: 슬래시 없는 상대경로('foo')는 상대 이동이 아니라 루트 기준('/foo')으로 절대화된다
//       (푸시/딥링크는 탭 시점 화면이 불정이라 상대 이동이 무의미).
// 네이티브가 목적지-ACK 를 짝지을 수 있도록 상관 id 를 회수한다(없으면 생략).
// 문자열로 정규화한다 — 같은 id 가 42 와 "42" 로 오가면 재전달 판별이 깨져
// 같은 건에 dropped 와 pending 을 연달아 보내게 된다.
export const getPushAckId = (data = {}) => {
  const value = getPayloadValue(
    data, 'ack_id', 'ackId', 'push_id', 'pushId', 'notification_id', 'notificationId'
  );
  return value === undefined ? undefined : String(value);
};

export const getDeepLinkPath = (data = {}) => {
  const lessonRequestPath = buildLessonRequestNotificationPath(data);
  if (lessonRequestPath) return lessonRequestPath;

  const raw =
    data.path ||
    data.action_url ||
    data.actionUrl ||
    data.deeplink ||
    data.deep_link;

  return resolveInternalPath(raw);
};

// payload 에서 최종 이동 경로(정규화 완료)를 뽑는다. 경로를 만들 수 없으면 null.
// 큐 배수와 live handler 가 같은 값을 봐야 중복 판정이 갈리지 않는다.
const resolveDeepLinkTarget = (data) => {
  const path = getDeepLinkPath(data);
  return path ? stripSharingPrefix(path) : null;
};

// 두 목적지가 같은 push 인지 판별한다.
// 상관 id 가 있으면 id 가 진실이다 — 경로가 같아도 id 가 다르면 별개 알림이라 최신 1건이 이긴다.
// 양쪽 다 id 가 없는 legacy push 만 정규화된 경로로 대조한다. 한쪽만 id 를 가진 경우는
// 별개로 본다(발신측이 식별자를 붙인 새 알림).
const isSamePushTarget = (a, b) =>
  a.id !== undefined || b.id !== undefined
    ? a.id === b.id
    : a.path !== null && a.path === b.path;

const useDeepLink = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const userInfo = useUserStore((state) => state.userInfo);
  // 로그인 전에 도착한 목적지. 네이티브가 pending 건을 보존·재전달하므로 인메모리로 충분하다.
  const pendingRef = useRef(null);
  // 토큰은 localStorage 라 재렌더를 유발하지 않는다. 네이티브가 토큰만 뒤늦게 복원하는 경로
  // (window.onAuthToken)에서도 pending 을 소비하려면 토큰 변화를 직접 구독해야 한다.
  const [accessTokenTick, setAccessTokenTick] = useState(0);
  // 최근에 이동을 마친 목적지들 — `{ key, at }`. 네이티브의 재전송이 뒤늦게 도착해도
  // 히스토리를 중복 쌓지 않는다. race window 를 넘긴 기록은 만료시킨다.
  const routedRef = useRef([]);

  // 인증 여부는 렌더 시점 스냅샷이 아니라 **호출 시점에 동기로** 읽는다.
  // 첫 렌더와 구독 effect 사이에 네이티브가 토큰을 저장하면 이벤트를 놓치는데,
  // 스냅샷을 쓰면 그 뒤 도착한 푸시가 영영 pending 에 갇힌다.
  const isAuthenticatedNow = () =>
    Boolean(useUserStore.getState().userInfo) && Boolean(TokenManager.getAccessToken());

  const ack = (status, data, path = null) =>
    sendPushRouteAck({ status, path, pushId: getPushAckId(data) });

  const routedKey = (id, path) => `${id}::${path}`;

  const pruneRoutedWindow = (now) => {
    routedRef.current = routedRef.current.filter(
      (entry) => now - entry.at < ROUTED_DEDUPE_WINDOW_MS
    );
  };

  const wasRecentlyRouted = (id, path) => {
    if (id === undefined) return false;
    const now = Date.now();
    pruneRoutedWindow(now);
    return routedRef.current.some((entry) => entry.key === routedKey(id, path));
  };

  // 목적지 이동 + 기록 + terminal 통보를 한 곳에서 처리한다.
  // 최초 수신(processDeepLink)과 로그인 후 pending 소비가 각자 같은 절차를 흉내내면
  // 한쪽만 고쳐졌을 때 재전달 판정이 조용히 갈린다.
  // state 는 부트 큐(cold-start) 진입에만 실린다 — useGoBack 이 history 없는 화면에서
  // 홈 fallback 을 허용할지 판별하는 표식이다. live/pending 소비는 push 라 표식이 필요 없다.
  const routeTo = (data, path, { replace = false, state } = {}) => {
    const id = getPushAckId(data);
    navigateRef.current(path, { replace, state });
    if (id !== undefined) {
      const now = Date.now();
      pruneRoutedWindow(now);
      routedRef.current.push({ key: routedKey(id, path), at: now });
    }
    ack('routed', data, path);
  };

  useEffect(() => {
    const bumpAccessToken = () => setAccessTokenTick((tick) => tick + 1);
    window.addEventListener(ACCESS_TOKEN_CHANGE_EVENT, bumpAccessToken);
    return () => window.removeEventListener(ACCESS_TOKEN_CHANGE_EVENT, bumpAccessToken);
  }, []);

  // navigate 참조 최신화
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const dropPending = () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      ack('dropped', pending.data, pending.path);
    };

    // 목적지는 항상 최신 1건이 이긴다. 교체된 건은 dropped 로 알려 네이티브가 소비하게 한다.
    const processDeepLink = (data, { replace = false, state } = {}) => {
      const cleanPath = resolveDeepLinkTarget(data);
      if (!cleanPath) {
        // 경로를 못 만든 payload 때문에 멀쩡한 pending 목적지를 버리지는 않는다.
        ack('invalid', data);
        return;
      }

      const incomingId = getPushAckId(data);

      // 방금 이동을 마친 건이 겹쳐서 다시 도착했다(네이티브 재전달). 히스토리에 같은 화면을
      // 또 쌓지 않고, 네이티브의 남은 사본이 소비되도록 terminal ACK 만 다시 보낸다.
      // window 를 넘긴 재수신은 사용자가 알림을 다시 탭한 것으로 보고 정상 이동시킨다.
      if (wasRecentlyRouted(incomingId, cleanPath)) {
        ack('routed', data, cleanPath);
        return;
      }

      // 네이티브가 보존 중이던 pending 을 재전달한 경우다. 같은 건에 dropped 를 보내면
      // 네이티브가 목적지를 소비해버리므로 교체가 아니라 갱신으로 다룬다.
      // 상관 id 가 없는 payload 는 목적지 경로가 같으면 같은 건으로 본다.
      const pending = pendingRef.current;
      const isRedelivery =
        Boolean(pending) &&
        isSamePushTarget(
          { id: incomingId, path: cleanPath },
          { id: getPushAckId(pending.data), path: pending.path }
        );

      if (isRedelivery) {
        pendingRef.current = null;
      } else {
        dropPending();
      }

      if (requiresAuth(cleanPath) && !isAuthenticatedNow()) {
        pendingRef.current = { data, path: cleanPath };
        ack('pending', data, cleanPath);
        // 보관만 하고 화면을 그대로 두면 알림을 눌렀는데 아무 일도 일어나지 않은 것으로 보인다.
        // pending 은 non-terminal 이라 목적지 소유권은 네이티브에 남고, 여기서는 로그인만
        // 안내한다. 로그인이 성립하면 아래 effect 가 보관분을 소비해 원래 목적지로 이동한다.
        // 항상 replace 다 — 네이티브는 보존한 pending 을 웹뷰 로드마다 재전달할 수 있어(§4-1)
        // push 로 쌓으면 같은 /login 항목이 재전달 횟수만큼 히스토리에 누적된다.
        navigateRef.current(LOGIN_PATH, { replace: true });
        return;
      }

      routeTo(data, cleanPath, { replace, state });
    };

    // 큐에 쌓인 딥링크 처리 (React 마운트 전에 네이티브가 보낸 것)
    const queue = window.__deepLinkQueue || [];
    if (queue.length > 0) {
      const latest = queue[queue.length - 1];
      const latestTarget = { id: getPushAckId(latest), path: resolveDeepLinkTarget(latest) };
      queue.slice(0, -1).forEach((data) => {
        const target = { id: getPushAckId(data), path: resolveDeepLinkTarget(data) };
        // 같은 push 가 큐에 두 번 들어온 것은 교체가 아니다. dropped 를 보내면
        // 네이티브가 곧 처리될 목적지를 먼저 소비해버린다. 상관 id 가 없는 legacy push 도
        // live handler 와 같은 규칙(정규화된 경로 대조)으로 판별해야 한다 — 그러지 않으면
        // 로그인 필요 목적지가 큐에 두 번 들어왔을 때 첫 건의 terminal dropped 로
        // pending 소유권까지 함께 날아간다.
        if (isSamePushTarget(target, latestTarget)) return;
        // 교체돼서 버리는 건 dropped, 애초에 경로를 만들 수 없던 건 invalid — 네이티브 집계가 달라진다.
        // 이동 경로와 같은 정규화를 거쳐야 네이티브가 ACK 의 action_url 로 원 목적지를 대조할 수 있다.
        ack(target.path ? 'dropped' : 'invalid', data, target.path);
      });
      // replace 라 history idx 가 진입 시점 그대로 남는다. useGoBack 이 이 항목을 "네이티브 레이어가
      // 직접 연 페이지"와 구분해 홈으로 보낼 수 있도록 cold-start 표식을 history state 에 남긴다.
      processDeepLink(latest, { replace: true, state: COLD_START_ENTRY_STATE });
      window.__deepLinkQueue = [];
    }

    // React 핸들러로 교체
    window.onDeepLink = (data) => processDeepLink(data);

    return () => {
      // cleanup 시에도 큐 방식으로 fallback (재마운트 사이 딥링크 유실 방지)
      window.onDeepLink = (data) => {
        window.__deepLinkQueue = window.__deepLinkQueue || [];
        window.__deepLinkQueue.push(data);
      };
    };
    // 핸들러 설치와 부트 큐 배수는 마운트당 1회여야 한다. routeTo/wasRecentlyRouted 는 매
    // 렌더마다 새로 만들어지므로 deps 에 넣으면 effect 가 재실행돼 큐를 다시 배수한다.
    // 두 helper 는 ref 만 만지므로 첫 렌더의 참조를 계속 써도 동작이 같다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 로그인이 성립하면 보관해 둔 목적지를 소비한다.
  useEffect(() => {
    if (!isAuthenticatedNow()) return;
    const pending = pendingRef.current;
    if (!pending) return;

    pendingRef.current = null;
    routeTo(pending.data, pending.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, accessTokenTick]);
};

export default useDeepLink;
